import React, { useState, useEffect, useRef, useCallback } from "react";

export default function GalleryViewer({
  gallerySlug,
  title = "Billeder af arbejdet",
  subtitle = "Se eksempler på vores udførte arbejde i marken",
  maxImages = null,
}) {
  const [images, setImages] = useState([]);
  const [galleryName, setGalleryName] = useState("");
  const [galleryDesc, setGalleryDesc] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  // Slider controls
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/galleries");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            const matched = data.find(
              (g) =>
                g.category === gallerySlug ||
                g.slug?._ === gallerySlug ||
                g.name?.toLowerCase().includes(gallerySlug?.toLowerCase() || "")
            );

            if (matched && Array.isArray(matched.images)) {
              setImages(matched.images);
              setGalleryName(matched.name);
              setGalleryDesc(matched.description);
            } else if (gallerySlug === "all") {
              // Combine all gallery images
              const allImgs = data.flatMap((g) => g.images || []);
              // Deduplicate
              const unique = Array.from(new Set(allImgs));
              setImages(unique);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load gallery from CMS:", err);
      } finally {
        setLoading(false);
      }
    }

    if (gallerySlug) {
      fetchGallery();
    }
  }, [gallerySlug]);

  const displayImages = maxImages ? images.slice(0, maxImages) : images;
  const isSlider = displayImages.length > 4;

  const checkScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  }, []);

  useEffect(() => {
    if (!isSlider) return;
    const el = sliderRef.current;
    if (!el) return;

    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [isSlider, displayImages.length, checkScroll]);

  const handleScroll = (direction) => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const firstChild = container.firstElementChild;
    const step = firstChild ? firstChild.getBoundingClientRect().width + 16 : 300;

    container.scrollBy({
      left: direction === "next" ? step : -step,
      behavior: "smooth",
    });
  };

  // Keyboard navigation in lightbox
  useEffect(() => {
    function handleKeyDown(e) {
      if (activeImageIndex === null) return;
      if (e.key === "Escape") setActiveImageIndex(null);
      if (e.key === "ArrowRight") {
        setActiveImageIndex((prev) => (prev + 1) % images.length);
      }
      if (e.key === "ArrowLeft") {
        setActiveImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImageIndex, images.length]);

  if (loading) {
    return (
      <div className="py-12 text-center text-stone-400">
        <i className="fa-solid fa-spinner fa-spin text-2xl mb-2 text-[#165B4C]"></i>
        <p className="text-xs">Indlæser billeder fra galleri...</p>
      </div>
    );
  }

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="py-8">
      {/* Header section with title and optional slider arrows */}
      {title && (
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 border-b border-stone-200/80 pb-4">
          <div>
            <h3 className="text-2xl font-bold text-stone-900 tracking-tight">{title}</h3>
            {galleryDesc ? (
              <p className="text-stone-600 text-sm mt-1">{galleryDesc}</p>
            ) : subtitle ? (
              <p className="text-stone-600 text-sm mt-1">{subtitle}</p>
            ) : null}
          </div>
        </div>
      )}

      {/* Main Images Display: Slider if > 4, Grid if <= 4 */}
      {isSlider ? (
        <div className="relative group/slider">
          {/* Floating Left Arrow */}
          <button
            type="button"
            onClick={() => handleScroll("prev")}
            disabled={!canScrollLeft}
            aria-label="Forrige billede"
            className={`absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white text-stone-800 border border-stone-200/90 shadow-lg flex items-center justify-center transition-all duration-200 cursor-pointer ${
              canScrollLeft
                ? "opacity-95 hover:opacity-100 hover:bg-[#165B4C] hover:text-white hover:scale-105 hover:border-[#165B4C]"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true"></i>
          </button>

          {/* Floating Right Arrow */}
          <button
            type="button"
            onClick={() => handleScroll("next")}
            disabled={!canScrollRight}
            aria-label="Næste billede"
            className={`absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white text-stone-800 border border-stone-200/90 shadow-lg flex items-center justify-center transition-all duration-200 cursor-pointer ${
              canScrollRight
                ? "opacity-95 hover:opacity-100 hover:bg-[#165B4C] hover:text-white hover:scale-105 hover:border-[#165B4C]"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true"></i>
          </button>

          {/* Horizontal Slider Track - Always in 1 line with 4 visible items on desktop */}
          <div
            ref={sliderRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth scrollbar-none py-1 -my-1 snap-x snap-mandatory"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {displayImages.map((imgUrl, index) => {
              const optimizedSrc = `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${imgUrl.replace(
                "https://assets.auxodev.dk/",
                ""
              )}`;

              return (
                <div
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className="snap-start shrink-0 w-[82%] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)] group relative aspect-4/3 rounded-xl overflow-hidden bg-stone-100 border border-stone-200/70 shadow-2xs hover:shadow-md transition-all cursor-pointer"
                >
                  <img
                    src={optimizedSrc}
                    alt={`${galleryName || "Arbejdsbillede"} ${index + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-[#0C332A]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center text-sm transform scale-90 group-hover:scale-100 transition-transform">
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Static Grid when 4 or fewer images */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {displayImages.map((imgUrl, index) => {
            const optimizedSrc = `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${imgUrl.replace(
              "https://assets.auxodev.dk/",
              ""
            )}`;

            return (
              <div
                key={index}
                onClick={() => setActiveImageIndex(index)}
                className="group relative aspect-4/3 rounded-xl overflow-hidden bg-stone-100 border border-stone-200/70 shadow-2xs hover:shadow-md transition-all cursor-pointer"
              >
                <img
                  src={optimizedSrc}
                  alt={`${galleryName || "Arbejdsbillede"} ${index + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-[#0C332A]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center text-sm transform scale-90 group-hover:scale-100 transition-transform">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Fullscreen Lightbox Modal */}
      {activeImageIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-stone-950/90 backdrop-blur-md flex items-center justify-center p-4 select-none animate-in fade-in duration-200"
          onClick={() => setActiveImageIndex(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Bar inside modal */}
            <div className="w-full flex items-center justify-between text-white pb-3 px-2">
              <span className="text-sm font-medium text-stone-300">
                Billede {activeImageIndex + 1} af {images.length}
              </span>
              <button
                onClick={() => setActiveImageIndex(null)}
                className="p-2 text-stone-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Luk visning"
              >
                <i className="fa-solid fa-xmark text-2xl" aria-hidden="true"></i>
              </button>
            </div>

            {/* Main Lightbox Image */}
            <div className="relative w-full flex items-center justify-center overflow-hidden rounded-2xl bg-black/40">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/${images[
                  activeImageIndex
                ].replace("https://assets.auxodev.dk/", "")}`}
                alt={`Forstørret billede ${activeImageIndex + 1}`}
                className="max-h-[75vh] max-w-full object-contain shadow-2xl rounded-lg"
              />

              {/* Prev Button */}
              {images.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex(
                      (prev) => (prev - 1 + images.length) % images.length
                    );
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-lg backdrop-blur-xs transition-all cursor-pointer"
                  aria-label="Forrige billede"
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
                </button>
              )}

              {/* Next Button */}
              {images.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex((prev) => (prev + 1) % images.length);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center text-lg backdrop-blur-xs transition-all cursor-pointer"
                  aria-label="Næste billede"
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                </button>
              )}
            </div>

            {/* Bottom thumbnail strip */}
            {images.length > 1 && (
              <div className="w-full flex items-center justify-center gap-2 mt-4 overflow-x-auto py-1 px-4">
                {images.map((thumb, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-14 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-all cursor-pointer ${
                      idx === activeImageIndex
                        ? "border-emerald-400 scale-105"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=70,format=auto/${thumb.replace(
                        "https://assets.auxodev.dk/",
                        ""
                      )}`}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
